import { css } from 'styled-components';
import lightThemeColors from '@prisjakt/design-tokens/light-theme-colors.json';
import darkThemeColors from '@prisjakt/design-tokens/dark-theme-colors.json';
import measurements from '@prisjakt/design-tokens/measurements.json';
import { setRgbaAlpha } from '../src/color-utils';

const BASE_FONT_SIZE = parseInt(measurements.root, 10);
const BASE_SPACING = BASE_FONT_SIZE / 2;
const PADDING_WRAPPER = 20;
const MARGIN_TOP_SUB_COMPONENT = 30;
const MARGIN_BOTTOM_SUB_COMPONENT = 30;
const GLOBAL_WIDTH = 1000;

// Transforms a pixel value to rem
// round to 5 digits to match Foundation rem-calc
export function remCalc(pixelSize: number) {
  const remSize = pixelSize / BASE_FONT_SIZE;
  return `${remSize.toFixed(5)}rem`;
}

// Transforms a rem value to pixel (without px unit)
export function pixelCalc(remSize: string) {
  return parseFloat(remSize) * BASE_FONT_SIZE;
}

export const colors = {
  white: lightThemeColors.base0,
  semiWhite: lightThemeColors.base100,
  darkBlue1: lightThemeColors.base700,
  darkBlue2: lightThemeColors.base700,
  darkBlue3: lightThemeColors.base700,
  lightBlue1: lightThemeColors.maybeBlue700,
  lightBlue2: lightThemeColors.maybeBlue700,
  lightBlue3: lightThemeColors.maybeBlue700,
  lightBlue4: lightThemeColors.maybeBlue300,
  green1: lightThemeColors.positive500,
  green2: lightThemeColors.positive500,
  green3: lightThemeColors.positive700,
  green4: lightThemeColors.positive300,
  darkGreen1: lightThemeColors.positive700,
  lightGray1: lightThemeColors.base200,
  lightGray2: lightThemeColors.base300,
  mediumGray1: lightThemeColors.base400,
  mediumGray2: lightThemeColors.base500,
  darkGray1: lightThemeColors.base500,
  darkGray2: lightThemeColors.base500,
  darkGray3: lightThemeColors.base600,
  darkGray4: lightThemeColors.base600,
  yellow1: lightThemeColors.yellove500,
  yellow2: lightThemeColors.yellove500,
  yellow3: lightThemeColors.yellove500,
  yellow4: lightThemeColors.yellove900,
  yellow5: lightThemeColors.notice300,
  red1: lightThemeColors.negative700,
  red2: lightThemeColors.negative700,
  red3: lightThemeColors.negative700,
  red4: lightThemeColors.negative300,
  backgroundDarkGray: setRgbaAlpha(lightThemeColors.base800, '0.05'),
  backgroundLightGray: 'rgba(0, 0, 0, 0.06)',
  borderDarkGray: 'rgba(0, 0, 0, 0.85);',
  borderLightGray: 'rgba(0, 0, 0, 0.09);',
  lightTheme: lightThemeColors,
  darkTheme: darkThemeColors,
};

const layoutMap = {
  measurements,
  fontSizes: {
    base: {
      mobile: {
        value: parseInt(measurements.fontSizeNormal, 10),
        rem: measurements.fontSizeNormal,
      },
      desktop: {
        value: parseInt(measurements.fontSizeNormal, 10),
        rem: measurements.fontSizeNormal,
      },
    },
    small: {
      mobile: {
        value: parseInt(measurements.fontSizeSmall, 10),
        rem: measurements.fontSizeSmall,
      },
      desktop: {
        value: parseInt(measurements.fontSizeSmall, 10),
        rem: measurements.fontSizeSmall,
      },
    },
    h1: {
      desktop: { value: 64, rem: remCalc(64) },
      mobile: { value: 36, rem: remCalc(36) },
    },
    h2: {
      desktop: { value: 24, rem: remCalc(24) },
      mobile: { value: 20, rem: remCalc(20) },
    },
    h3: {
      desktop: { value: 18, rem: remCalc(18) },
      mobile: { value: 18, rem: remCalc(18) },
    },
    h4: {
      desktop: { value: 17, rem: remCalc(17) },
      mobile: { value: 17, rem: remCalc(17) },
    },
    h5: {
      desktop: { value: 17, rem: remCalc(17) },
      mobile: { value: 17, rem: remCalc(17) },
    },
    h6: {
      desktop: { value: 16, rem: remCalc(16) },
      mobile: { value: 16, rem: remCalc(16) },
    },
  },

  margins: {
    wrapper: {
      mobile: { value: 12, rem: remCalc(12) },
      desktop: { value: 10, rem: remCalc(10) },
    },

    component: {
      mobile: { value: 20, rem: remCalc(20) },
      desktop: { value: 30, rem: remCalc(30) },
    },
  },

  headerHeight: {
    desktop: { value: 72, rem: remCalc(72) },
    mobile: { value: 48, rem: remCalc(48) },
    mobileWithSearch: { value: 101, rem: remCalc(101) },
  },

  fontFamily: 'Mulish, Helvetica Neue, Helvetica, Arial, Roboto, sans-serif',

  sidebarContentWidth: {
    desktop: { value: 304, rem: remCalc(304) },
  },

  sidebarMenuWidth: {
    desktop: { value: 56, rem: remCalc(56) },
  },

  pageContent: {
    gutter: { value: 10, rem: remCalc(10) },
    maxWidth: { value: 1300, rem: remCalc(1300) },
  },

  siteWidth: { value: GLOBAL_WIDTH, rem: remCalc(GLOBAL_WIDTH) },
  borderRadius: '5px',
  lineHeight: 1.5,

  stickyNavigationHeight: { value: 50, rem: remCalc(50) },

  dragAndDropHandleSize: {
    mobile: {
      value: 32,
      rem: remCalc(32),
    },
    desktop: { value: 16, rem: remCalc(16) },
  },

  pageContentMaxWidthVal: 1300,
  pageContentGutterVal: 10,

  // Ad layout
  takeOverTopMargin: '120px',
  takeOverWidth: '1600px',
  takeOverHeight: '900px',
  outsiderMinWidth: '200px',

  listSideMargin: '0.6rem',
  listSidePadding: '0.6rem',
  listSideHeight: '2.6rem',

  // Product card
  productCardMinWidthMobile: '146px',
  productCardMinWidth: '200px',
  productCardMaxWidth: '255px',
  productCardImageSizeSmall: '90px',
  productCardImageSizeLarge: '130px',
  productCardPadding: '1rem',

  // Foundation grid
  gridColumnGutter: {
    small: { value: 25, rem: remCalc(25) },
    large: { value: PADDING_WRAPPER, rem: remCalc(PADDING_WRAPPER) },
  },

  // Search
  searchBar: {
    height: {
      desktop: {
        value: 45,
        rem: remCalc(45),
      },
      mobile: {
        value: 35,
        rem: remCalc(35),
      },
    },
    borderWidth: 1,
  },

  // Spacing
  spacing: {
    xxSmall: `calc(0.5 * ${remCalc(BASE_SPACING)})`,
    xSmall: `calc(1 * ${remCalc(BASE_SPACING)})`,
    small: `calc(2 * ${remCalc(BASE_SPACING)})`,
    medium: `calc(3 * ${remCalc(BASE_SPACING)})`,
    large: `calc(4 * ${remCalc(BASE_SPACING)})`,
    xLarge: `calc(5 * ${remCalc(BASE_SPACING)})`,
    xxLarge: `calc(6 * ${remCalc(BASE_SPACING)})`,
    values: {
      xxSmall: 0.5 * BASE_SPACING,
      xSmall: 1 * BASE_SPACING,
      small: 2 * BASE_SPACING,
      medium: 3 * BASE_SPACING,
      large: 4 * BASE_SPACING,
      xLarge: 5 * BASE_SPACING,
      xxLarge: 6 * BASE_SPACING,
    },
    rem: {
      xxSmall: (0.5 * BASE_SPACING) / BASE_FONT_SIZE,
      xSmall: (1 * BASE_SPACING) / BASE_FONT_SIZE,
      small: (2 * BASE_SPACING) / BASE_FONT_SIZE,
      medium: (3 * BASE_SPACING) / BASE_FONT_SIZE,
      large: (4 * BASE_SPACING) / BASE_FONT_SIZE,
      xLarge: (5 * BASE_SPACING) / BASE_FONT_SIZE,
      xxLarge: (6 * BASE_SPACING) / BASE_FONT_SIZE,
    },
  },

  animations: {
    buttonTransition: '150ms ease',
  },
};

const layoutMapDeprecated = {
  paddingWrapperVal: PADDING_WRAPPER,
  paddingWrapper: remCalc(PADDING_WRAPPER),
  marginTopSubComponentVal: MARGIN_TOP_SUB_COMPONENT,
  marginTopSubComponent: remCalc(MARGIN_TOP_SUB_COMPONENT),
  marginBottomSubComponentVal: MARGIN_BOTTOM_SUB_COMPONENT,
  marginBottomSubComponent: remCalc(MARGIN_BOTTOM_SUB_COMPONENT),
};

const fullLayoutMap = {
  ...layoutMapDeprecated,
  ...layoutMap,
};

export const layout =
  process.env.NODE_ENV === 'development'
    ? new Proxy(fullLayoutMap, {
        get: (obj, prop) => {
          if (prop in layoutMapDeprecated) {
            // eslint-disable-next-line no-console
            console.warn(
              `Deprecation: layout['${String(
                prop
              )}'] is deprecated in base, please refer to @prisjakt/ui-base to see the new value.`
            );
          }
          return obj[prop];
        },
      })
    : fullLayoutMap;

// Foundation breakpoints
export const breakpoints = {
  smallOnly: 'screen and (max-width: 39.9375em)',
  mediumUp: 'screen and (min-width: 40em)',
  mediumOnly: 'screen and (max-width: 63.9375em) and (min-width: 40em)',
  mediumDown: 'screen and (max-width: 63.9375em)',
  largeUp: 'screen and (min-width: 64em)',
  largeOnly: 'screen and (max-width: 74.9375em) and (min-width: 64em)',
  hideOutsiderAd: 'screen and (max-width: 1199px)',
  showOutsiderAd: 'screen and (min-width: 1200px)',
};

// Create media queries from a list of breakpoints
type MediaType = {
  [key in keyof typeof breakpoints]: typeof css;
};
type Parameters<T> = T extends (...args: infer T) => any ? T : never;
export const media: MediaType = Object.entries(breakpoints).reduce(
  (accumulator, [key, value]) => ({
    ...accumulator,
    [key]: ((...args: Parameters<typeof css>) => css`
      @media ${value} {
        ${css(...args)};
      }
    `) as typeof css,
  }),
  {} as MediaType
);
